import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Header from '../components/Header';

const Admin = () => {
  const [pendingSellers, setPendingSellers] = useState([]);
  const [approvedSellers, setApprovedSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'approved'
  const { currentUser } = useAuth();

  const isAdmin = currentUser?.uid === 'LKwrmb4h5cSI5qo8sQEmNZ257k82';

  const fetchPendingSellers = async () => {
    setLoadingSellers(true);
    try {
      const q = query(collection(db, 'sellerReviews'), where('status', '==', 'pending'));
      const querySnapshot = await getDocs(q);
      const sellers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPendingSellers(sellers);
    } catch (error) {
      console.error('Error fetching pending sellers:', error);
    } finally {
      setLoadingSellers(false);
    }
  };

  const fetchApprovedSellers = async () => {
    setLoadingSellers(true);
    try {
      const q = query(collection(db, 'sellerReviews'), where('status', '==', 'approved'));
      const querySnapshot = await getDocs(q);
      const sellers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setApprovedSellers(sellers);
    } catch (error) {
      console.error('Error fetching approved sellers:', error);
    } finally {
      setLoadingSellers(false);
    }
  };

  const approveSeller = async (sellerId) => {
    try {
      // Update seller review status
      await updateDoc(doc(db, 'sellerReviews', sellerId), {
        status: 'approved',
        reviewedAt: new Date().toISOString(),
        reviewedBy: currentUser.uid
      });

      // Update user profile to approved
      await updateDoc(doc(db, 'users', sellerId), {
        approved: true,
        updatedAt: new Date().toISOString()
      });

      // Refresh the pending sellers list
      await fetchPendingSellers();
      setSelectedSeller(null);
    } catch (error) {
      console.error('Error approving seller:', error);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchPendingSellers();
      fetchApprovedSellers();
    }
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-abiu-green-bg flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access the admin panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* <Header /> */}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Panel</h1>
          <p className="text-gray-600">Review and manage seller applications</p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => {
                  setActiveTab('pending');
                  setSelectedSeller(null);
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pending'
                    ? 'border-abiu-green text-abiu-green'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending Applications ({pendingSellers.length})
              </button>
              <button
                onClick={() => {
                  setActiveTab('approved');
                  setSelectedSeller(null);
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'approved'
                    ? 'border-abiu-green text-abiu-green'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Approved Sellers ({approvedSellers.length})
              </button>
            </nav>
          </div>
        </div>

        {loadingSellers ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-abiu-green border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">Loading pending sellers...</span>
          </div>
        ) : (activeTab === 'pending' ? pendingSellers.length === 0 : approvedSellers.length === 0) ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {activeTab === 'pending' ? 'No Pending Applications' : 'No Approved Sellers'}
            </h3>
            <p className="text-gray-600">
              {activeTab === 'pending' ? 'All seller applications have been reviewed.' : 'No sellers have been approved yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sellers List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {activeTab === 'pending' ? 'Pending Applications' : 'Approved Sellers'} ({activeTab === 'pending' ? pendingSellers.length : approvedSellers.length})
                </h2>
              </div>
              <div className="divide-y divide-gray-200 max-h-[70vh] overflow-y-auto">
                {(activeTab === 'pending' ? pendingSellers : approvedSellers).map((seller) => (
                  <div
                    key={seller.id}
                    className={`p-6 cursor-pointer transition-colors ${
                      selectedSeller?.id === seller.id ? 'bg-abiu-green-light/20 border-l-4 border-abiu-green' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedSeller(seller)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{seller.name}</h3>
                        <p className="text-sm text-gray-600 mb-2">{seller.email}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>{seller.phone}</span>
                          <span>•</span>
                          <span>{seller.location}</span>
                          <span>•</span>
                          <span className="capitalize">{seller.sellerType}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(seller.submittedAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Seller Details */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {selectedSeller ? (
                <div className="p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 mb-1">{selectedSeller.name}</h2>
                      <p className="text-gray-600">{selectedSeller.email}</p>
                    </div>
                    {activeTab === 'pending' && (
                      <button
                        onClick={() => approveSeller(selectedSeller.id)}
                        className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors font-medium"
                      >
                        Approve Seller
                      </button>
                    )}
                    {activeTab === 'approved' && (
                      <div className="flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Approved
                      </div>
                    )}
                  </div>

                  <div className="space-y-6">
                    {/* Contact Information */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="block text-sm font-medium text-gray-700">Phone</span>
                          <span className="text-gray-900">{selectedSeller.phone}</span>
                        </div>
                        <div>
                          <span className="block text-sm font-medium text-gray-700">Location</span>
                          <span className="text-gray-900">{selectedSeller.location}</span>
                        </div>
                        <div>
                          <span className="block text-sm font-medium text-gray-700">Business Type</span>
                          <span className="text-gray-900 capitalize">{selectedSeller.sellerType}</span>
                        </div>
                        <div>
                          <span className="block text-sm font-medium text-gray-700">Years in Business</span>
                          <span className="text-gray-900">{selectedSeller.yearsInBusiness || 'Not specified'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Online Presence */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Online Presence</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="block text-sm font-medium text-gray-700">Website</span>
                          {selectedSeller.website ? (
                            <a 
                              href={selectedSeller.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-abiu-green hover:text-abiu-green-dark underline"
                            >
                              {selectedSeller.website}
                            </a>
                          ) : (
                            <span className="text-gray-500">Not provided</span>
                          )}
                        </div>
                        <div>
                          <span className="block text-sm font-medium text-gray-700">Facebook</span>
                          <span className="text-gray-900">{selectedSeller.facebook || 'Not provided'}</span>
                        </div>
                        <div>
                          <span className="block text-sm font-medium text-gray-700">Instagram</span>
                          <span className="text-gray-900">{selectedSeller.instagram || 'Not provided'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Business Details */}
                    {selectedSeller.businessDescription && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Business Description</h3>
                        <p className="text-gray-900 leading-relaxed">{selectedSeller.businessDescription}</p>
                      </div>
                    )}

                    {selectedSeller.specializations && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Specializations</h3>
                        <p className="text-gray-900">{selectedSeller.specializations}</p>
                      </div>
                    )}

                    {/* Application Details */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Application Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="block text-sm font-medium text-gray-700">Submitted</span>
                          <span className="text-gray-900">
                            {new Date(selectedSeller.submittedAt).toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="block text-sm font-medium text-gray-700">User ID</span>
                          <span className="text-gray-900 font-mono text-sm">{selectedSeller.userId}</span>
                        </div>
                        {activeTab === 'approved' && selectedSeller.reviewedAt && (
                          <>
                            <div>
                              <span className="block text-sm font-medium text-gray-700">Approved</span>
                              <span className="text-gray-900">
                                {new Date(selectedSeller.reviewedAt).toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <span className="block text-sm font-medium text-gray-700">Reviewed By</span>
                              <span className="text-gray-900 font-mono text-sm">{selectedSeller.reviewedBy}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Notes Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Admin Notes</h3>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
                        rows="3"
                        placeholder="Add notes about this application..."
                        defaultValue={selectedSeller.notes || ''}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <p>Select a seller from the list to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin; 